import gsap from "gsap";
import { highlightCodeLines } from "./codemirror";
import {
  computeStageLineCountWithFStar,
  computeViewportIndicatorsWithFStar,
} from "./history-viewer-fstar";
import type { ChangeIndicator } from "./history-viewer-types";
import {
  buildMotionLines as pureBuildMotionLines,
  buildTransitionFrame as pureBuildTransitionFrame,
  computeNewlineHighlighting,
  splitLines as pureSplitLines,
  buildTransitionLines as pureBuildTransitionLines,
  progressFromIndex as pureProgressFromIndex,
  sameTimeline as pureSameTimeline,
  type PureMotionLine,
  type PureTransitionFrame,
} from "./history-viewer-pure";
// HistoryPayload + HistoryCommitFrame are paradox-generated from
// `core.dox`. The local interfaces have been dropped — the
// JSON.parse boundary in parseHistoryPayload runs through
// validHistoryPayload so a malformed payload throws once instead of
// returning a falsey `null` that downstream code interprets as "no
// history yet."
import {
  validHistoryPayload,
  type HistoryPayload,
  type HistoryCommitFrame,
} from "../../.dox/runtime";

// Re-export the moved helpers under their original names so call sites
// (tests, imports inside this file) keep working. Definitions now live
// in history-viewer-pure.ts.
export const splitLines = pureSplitLines;
export const buildTransitionLines = pureBuildTransitionLines;
export const progressFromIndex = pureProgressFromIndex;
export const sameTimeline = pureSameTimeline;
export const buildMotionLines = pureBuildMotionLines;
export const buildTransitionFrame = pureBuildTransitionFrame;

// Renderer-side aliases for the structural types defined in
// history-viewer-pure.ts. Specialised to the dox HistoryCommitFrame
// since the viewer is the unique consumer that needs the full commit
// shape; the pure module stays generic for test fixtures.
type MotionLine = PureMotionLine;
type TransitionFrame = PureTransitionFrame<HistoryCommitFrame>;

export const parseHistoryPayload = (
  raw: string | null | undefined,
): HistoryPayload | null => {
  if (!raw) return null;
  try {
    // Paradox treats `Text?` as "may be null but key MUST be present";
    // legacy fixtures and lazy timeline payloads omit those nullable
    // keys. Backfill them before generated validation so stricter codegen
    // keeps accepting the same wire payloads.
    const raw_ = JSON.parse(raw) as Record<string, unknown>;
    if (raw_["activeContent"] === undefined) raw_["activeContent"] = "";
    const commits = raw_["commits"];
    if (Array.isArray(commits)) {
      commits.forEach((commit) => {
        if (
          commit === null ||
          typeof commit !== "object" ||
          Array.isArray(commit)
        ) {
          return;
        }
        const frame = commit as Record<string, unknown>;
        if (frame["path"] === undefined) frame["path"] = null;
        if (frame["blobSha"] === undefined) frame["blobSha"] = null;
        if (frame["content"] === undefined) frame["content"] = null;
      });
    }
    const parsed = validHistoryPayload(raw_);
    return {
      ...parsed,
      activeContent: parsed.activeContent ?? "",
    };
  } catch {
    return null;
  }
};

const decodeBase64Utf8 = (raw: string): string => {
  const binary = window.atob(raw);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const parseHistoryPayloadBase64 = (
  raw: string | null | undefined,
): HistoryPayload | null => {
  if (!raw) return null;

  try {
    return parseHistoryPayload(decodeBase64Utf8(raw));
  } catch {
    return null;
  }
};

export const findCommitIndex = (
  commits: readonly HistoryCommitFrame[],
  hash: string,
): number => commits.findIndex((commit) => commit.hash === hash);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const isWaypointPosition = (
  position: number,
  commitCount: number,
  epsilon = 0.035,
): boolean => {
  if (commitCount <= 1) return true;
  const rounded = Math.round(position);
  return Math.abs(position - rounded) <= epsilon;
};

export const selectionWeight = (position: number, index: number): number =>
  clamp(1 - Math.abs(position - index), 0, 1);

export const shouldSettleToTarget = (
  position: number,
  targetIndex: number | null,
  epsilon = 0.18,
): boolean =>
  targetIndex !== null && Math.abs(position - targetIndex) <= epsilon;

const HISTORY_LINE_HEIGHT = 24;
const HISTORY_LINE_SLIDE = 96;
const HISTORY_NAV_TWEEN_BASE = 1;
const HISTORY_NAV_TWEEN_MAX = 4;
const HISTORY_DIRECTION_EPSILON = 0.03;
const HISTORY_LINE_STAGGER_EACH = 0.05;
const HISTORY_LINE_EXIT_DURATION = 0.82;
const HISTORY_LINE_ENTER_DURATION = 0.82;
const HISTORY_LINE_ENTER_DELAY = 0.18;
// Diff colors map onto the theme's success/danger tokens emitted by
// paradox CSS codegen — same `r g b` triple as `var(--c-*)`, but read
// imperatively because canvas/SVG style strings here are concatenated
// rather than carried through CSS.
const readCssColor = (prop: string, fallback: string): string => {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(prop)
    .trim();
  return raw || fallback;
};
const diffAddRgb = (): string => readCssColor("--c-success", "134 239 172");
const diffDelRgb = (): string => readCssColor("--c-danger", "248 113 113");
const HISTORY_ADD_INDICATOR = (): string => `rgb(${diffAddRgb()} / 0.95)`;
const HISTORY_REMOVE_INDICATOR = (): string => `rgb(${diffDelRgb()} / 0.95)`;
const HISTORY_GUTTER_COLOR = (): string => `rgb(${diffAddRgb()} / 0.5)`;
// Stage has px-4 (16px) — gutter offsets into it to sit at the viewer edge.
const HISTORY_GUTTER_STAGE_PAD = 16;

const motionLineStaggerCount = (motionLines: readonly MotionLine[]): number =>
  Math.max(
    motionLines.filter((line) => line.kind === "added").length,
    motionLines.filter((line) => line.kind === "removed").length,
    1,
  );

const navTweenDuration = (staggerCount: number): number =>
  clamp(
    0.3 * Math.sqrt(staggerCount),
    HISTORY_NAV_TWEEN_BASE,
    HISTORY_NAV_TWEEN_MAX,
  );

// Kept here (and not in the pure module) because the renderer is its
// only call site: `navTweenDuration` consumes the count to drive a
// gsap.to() in scrollToHash. The pure module already inlines the same
// math inside `buildTransitionFrame.staggerCount`.

interface MotionLineStyle {
  top: number;
  opacity: number;
  x: number;
}

interface TransitionRowElements {
  key: string;
  row: HTMLDivElement;
  content: HTMLDivElement;
}

interface ViewportIndicators {
  top: ChangeIndicator;
  bottom: ChangeIndicator;
}

let historyHighlightRenderer: (
  code: string,
  lang: string,
) => Promise<string[]> = highlightCodeLines;

export const setHistoryHighlightRendererForTest = (
  renderer: ((code: string, lang: string) => Promise<string[]>) | null,
): void => {
  historyHighlightRenderer = renderer ?? highlightCodeLines;
};

const lerp = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress;

const easeInOutCubic = (value: number): number =>
  value < 0.5 ? 4 * value * value * value : 1 - (-2 * value + 2) ** 3 / 2;

const easeOutCubic = (value: number): number => 1 - (1 - value) ** 3;

const staggerProgress = (
  progress: number,
  staggerIndex: number,
  start: number,
  duration: number,
  timelineDuration: number,
): number => {
  const offset = staggerIndex * HISTORY_LINE_STAGGER_EACH;
  const absoluteTime = progress * timelineDuration;
  return clamp(
    (absoluteTime - start - offset) / Math.max(duration, 0.001),
    0,
    1,
  );
};

const transitionTimelineDuration = (staggerCount: number): number => {
  const lastOffset = Math.max(staggerCount - 1, 0) * HISTORY_LINE_STAGGER_EACH;
  return Math.max(
    1,
    lastOffset + HISTORY_LINE_EXIT_DURATION,
    HISTORY_LINE_ENTER_DELAY + lastOffset + HISTORY_LINE_ENTER_DURATION,
  );
};

export const measureMotionLine = (
  line: MotionLine,
  progress: number,
  direction: 1 | -1,
  staggerCount: number,
  rowHeight = HISTORY_LINE_HEIGHT,
): MotionLineStyle => {
  const timelineDuration = transitionTimelineDuration(staggerCount);

  if (line.kind === "unchanged") {
    const move = easeInOutCubic(progress);
    return {
      top: lerp(line.fromIndex ?? 0, line.toIndex ?? 0, move) * rowHeight,
      opacity: 1,
      x: 0,
    };
  }

  if (line.kind === "removed") {
    const exit = easeInOutCubic(
      staggerProgress(
        progress,
        line.staggerIndex,
        0,
        HISTORY_LINE_EXIT_DURATION,
        timelineDuration,
      ),
    );
    return {
      top: (line.fromIndex ?? 0) * rowHeight,
      opacity: 1 - easeOutCubic(exit),
      x: -direction * HISTORY_LINE_SLIDE * easeOutCubic(exit),
    };
  }

  const enter = easeInOutCubic(
    staggerProgress(
      progress,
      line.staggerIndex,
      HISTORY_LINE_ENTER_DELAY,
      HISTORY_LINE_ENTER_DURATION,
      timelineDuration,
    ),
  );
  return {
    top: (line.toIndex ?? 0) * rowHeight,
    opacity: enter,
    x: direction * HISTORY_LINE_SLIDE * (1 - easeOutCubic(enter)),
  };
};

export const transitionStageLineCount = (frame: TransitionFrame): number => {
  const fromFStar = computeStageLineCountWithFStar(
    frame.oldLineCount,
    frame.newLineCount,
    frame.motionLines,
  );
  if (typeof fromFStar !== "number")
    throw new Error("[HistoryViewer] WASM unavailable: computeStageLineCount");
  return fromFStar;
};

export const transitionViewportIndicators = (
  frame: TransitionFrame,
  scrollTop: number,
  clientHeight: number,
  rowHeight = HISTORY_LINE_HEIGHT,
): ViewportIndicators => {
  const fromFStar = computeViewportIndicatorsWithFStar(
    scrollTop,
    clientHeight,
    rowHeight,
    frame.motionLines,
  );
  if (!fromFStar)
    throw new Error(
      "[HistoryViewer] WASM unavailable: computeViewportIndicators",
    );
  return fromFStar;
};

import type { EditorPushEventWire } from "./hooks/types";

interface LiveViewHookLike {
  el: HTMLElement;
  pushEvent(event: EditorPushEventWire, payload: Record<string, unknown>): void;
  handleEvent?(
    event: EditorPushEventWire,
    callback: (payload: unknown) => void,
  ): void;
}

interface HistoryViewerState {
  payload: HistoryPayload | null;
  navShellEl: HTMLElement | null;
  scrollEl: HTMLElement | null;
  stageEl: HTMLElement | null;
  codeEl: HTMLElement | null;
  snapTimer: number | null;
  lastHash: string | null;
  syncing: boolean;
  syncTargetIndex: number | null;
  pointerDown: boolean;
  scrollHandler: () => void;
  stageScrollHandler: () => void;
  wheelHandler: (event: WheelEvent) => void;
  keyHandler: (event: KeyboardEvent) => void;
  pointerDownHandler: () => void;
  pointerUpHandler: () => void;
  waypointClickHandler: (event: Event) => void;
  lastPosition: number | null;
  motionDirection: 1 | -1;
  transitionKey: string | null;
  transitionRows: TransitionRowElements[];
  transitionTimeline: gsap.core.Timeline | null;
  currentFrame: TransitionFrame | null;
  lang: string | null;
  renderVersion: number;
  staticCommitIndex: number | null;
  // Lazy snapshot cache. Seeded on mount with `[activeHash → activeContent]`
  // and grown on demand: when a commit's content is needed (waypoint
  // landing, transition endpoint), the resolver checks here first and
  // — on miss — schedules a `fetch_history_content` push so the server
  // streams the bytes for just that hash (batched across animation
  // frames). Any other commit's content stays out of the page payload
  // entirely, which is the whole point of the lazy mode.
  contentCache: Map<string, string>;
  pendingFetches: Map<string, string>;
  fetchTimer: number | null;
  hookRef: LiveViewHookLike | null;
}

const historyViewerState = new WeakMap<HTMLElement, HistoryViewerState>();

const getState = (el: HTMLElement): HistoryViewerState => {
  const existing = historyViewerState.get(el);
  if (existing) return existing;

  const state: HistoryViewerState = {
    payload: null,
    navShellEl: null,
    scrollEl: null,
    stageEl: null,
    codeEl: null,
    snapTimer: null,
    lastHash: null,
    syncing: false,
    syncTargetIndex: null,
    pointerDown: false,
    scrollHandler: () => undefined,
    stageScrollHandler: () => undefined,
    wheelHandler: () => undefined,
    keyHandler: () => undefined,
    pointerDownHandler: () => undefined,
    pointerUpHandler: () => undefined,
    waypointClickHandler: () => undefined,
    lastPosition: null,
    motionDirection: 1,
    transitionKey: null,
    transitionRows: [],
    transitionTimeline: null,
    currentFrame: null,
    lang: el.getAttribute("data-history-lang"),
    renderVersion: 0,
    staticCommitIndex: null,
    contentCache: new Map(),
    pendingFetches: new Map(),
    fetchTimer: null,
    hookRef: null,
  };

  historyViewerState.set(el, state);
  return state;
};

const renderCommit = (
  state: HistoryViewerState,
  content: string,
  commitIndex?: number,
): void => {
  if (!state.codeEl) return;
  // Already showing this commit in static mode — don't rebuild and destroy gutter transitions.
  if (
    commitIndex !== undefined &&
    state.staticCommitIndex === commitIndex &&
    state.currentFrame === null
  )
    return;
  state.staticCommitIndex = commitIndex ?? null;
  state.codeEl.style.position = "relative";
  state.transitionKey = null;
  state.transitionRows = [];
  state.transitionTimeline?.kill();
  state.transitionTimeline = null;
  state.currentFrame = null;
  state.renderVersion += 1;
  const renderVersion = state.renderVersion;
  if (state.stageEl) state.stageEl.style.boxShadow = "";

  // Compute which lines are new in this commit vs the previous one
  const commits = state.payload?.commits;
  const prevIndex =
    commitIndex !== undefined && commitIndex > 0 ? commitIndex - 1 : -1;
  const prevContent =
    prevIndex >= 0 && commits ? (commits[prevIndex]?.content ?? null) : null;
  const { addedSet } =
    prevContent !== null
      ? computeNewlineHighlighting(
          prevContent,
          content,
          buildMotionLines(prevContent, content),
        )
      : { addedSet: new Set<number>() as ReadonlySet<number> };
  const fragment = document.createDocumentFragment();
  const contentEls: HTMLDivElement[] = [];
  const gutterEls: HTMLDivElement[] = [];
  const lines = splitLines(content);

  // Use the same absolute-positioned row structure as renderTransition so that
  // the DOM swap between static and transition modes causes zero layout reflow.
  state.codeEl.style.height = `${lines.length * HISTORY_LINE_HEIGHT}px`;

  lines.forEach((line, index) => {
    const row = document.createElement("div");
    row.className = "absolute left-0 right-0";
    row.style.height = `${HISTORY_LINE_HEIGHT}px`;
    row.style.top = `${index * HISTORY_LINE_HEIGHT}px`;
    row.style.pointerEvents = "none";

    if (addedSet.has(index)) {
      const gutter = document.createElement("div");
      gutter.style.position = "absolute";
      gutter.style.left = `${-HISTORY_GUTTER_STAGE_PAD}px`;
      gutter.style.top = "0";
      gutter.style.width = "2px";
      gutter.style.height = "100%";
      gutter.style.backgroundColor = HISTORY_GUTTER_COLOR();
      gutter.style.opacity = "0";
      gutter.style.transition = "opacity 0.3s ease";
      row.appendChild(gutter);
      gutterEls.push(gutter);
    }

    const contentEl = document.createElement("div");
    contentEl.className = "whitespace-pre";
    contentEl.style.minHeight = `${HISTORY_LINE_HEIGHT}px`;
    contentEl.textContent = line === "" ? " " : line;
    row.appendChild(contentEl);
    contentEls.push(contentEl);

    fragment.appendChild(row);
  });

  // Replace children in one operation to avoid a blank-frame flash.
  state.codeEl.replaceChildren(fragment);

  // Fade in gutters — rAF lets the browser paint opacity: 0 first.
  if (gutterEls.length > 0) {
    requestAnimationFrame(() => {
      gutterEls.forEach((g) => {
        g.style.opacity = "1";
      });
    });
  }

  if (state.lang) {
    void historyHighlightRenderer(content, state.lang).then((lines) => {
      if (
        !state.codeEl ||
        state.currentFrame ||
        state.renderVersion !== renderVersion
      )
        return;
      lines.forEach((lineHtml, index) => {
        const el = contentEls[index];
        if (el) el.innerHTML = lineHtml;
      });
    });
  }
};

const applyStageIndicators = (
  state: HistoryViewerState,
  indicators: ViewportIndicators,
): void => {
  const stageEl = state.stageEl;
  if (!stageEl) return;

  const shadows: string[] = [];
  if (indicators.top === "added")
    shadows.push(`inset 0 2px 0 ${HISTORY_ADD_INDICATOR()}`);
  if (indicators.top === "removed")
    shadows.push(`inset 0 2px 0 ${HISTORY_REMOVE_INDICATOR()}`);
  if (indicators.bottom === "added")
    shadows.push(`inset 0 -2px 0 ${HISTORY_ADD_INDICATOR()}`);
  if (indicators.bottom === "removed")
    shadows.push(`inset 0 -2px 0 ${HISTORY_REMOVE_INDICATOR()}`);

  stageEl.style.boxShadow = shadows.join(", ");
};

const updateStageIndicators = (state: HistoryViewerState): void => {
  if (!state.stageEl || !state.currentFrame) {
    if (state.stageEl) state.stageEl.style.boxShadow = "";
    return;
  }

  applyStageIndicators(
    state,
    transitionViewportIndicators(
      state.currentFrame,
      state.stageEl.scrollTop,
      state.stageEl.clientHeight,
    ),
  );
};

const transitionFrameKey = (frame: TransitionFrame): string =>
  `${frame.from.hash}:${frame.to.hash}:${frame.direction}`;

const buildTransitionTimeline = (
  rows: readonly TransitionRowElements[],
  lines: readonly MotionLine[],
  direction: 1 | -1,
): gsap.core.Timeline => {
  const timeline = gsap.timeline({
    paused: true,
    defaults: { ease: "sine.inOut" },
  });

  const removedTargets = lines
    .map((line, index) => ({ line, content: rows[index]?.content }))
    .filter(
      (entry): entry is { line: MotionLine; content: HTMLDivElement } =>
        !!entry.content && entry.line.kind === "removed",
    )
    .sort((left, right) => left.line.staggerIndex - right.line.staggerIndex)
    .map((entry) => entry.content);
  const addedTargets = lines
    .map((line, index) => ({ line, content: rows[index]?.content }))
    .filter(
      (entry): entry is { line: MotionLine; content: HTMLDivElement } =>
        !!entry.content && entry.line.kind === "added",
    )
    .sort((left, right) => left.line.staggerIndex - right.line.staggerIndex)
    .map((entry) => entry.content);

  rows.forEach(({ content }, index) => {
    const line = lines[index];
    if (!line || line.kind !== "unchanged") return;
    content.style.opacity = "1";
    content.style.transform = "translate3d(0px, 0, 0)";
  });

  if (removedTargets.length > 0) {
    timeline.fromTo(
      removedTargets,
      { x: 0, opacity: 1 },
      {
        x: -direction * HISTORY_LINE_SLIDE,
        opacity: 0,
        duration: HISTORY_LINE_EXIT_DURATION,
        stagger: { each: HISTORY_LINE_STAGGER_EACH },
      },
      0,
    );
  }

  if (addedTargets.length > 0) {
    timeline.fromTo(
      addedTargets,
      { x: direction * HISTORY_LINE_SLIDE, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: HISTORY_LINE_ENTER_DURATION,
        stagger: { each: HISTORY_LINE_STAGGER_EACH },
      },
      HISTORY_LINE_ENTER_DELAY,
    );
  }

  return timeline;
};

// One transition row: a line's text in an absolutely placed row, tinted
// by its kind. The history viewer's scrub and a workspace change draw
// the same rows; the viewer then swaps highlighted HTML into `content`.
const buildTransitionRow = (line: MotionLine): TransitionRowElements => {
  const row = document.createElement("div");
  row.className = "absolute left-0 right-0";
  row.style.height = `${HISTORY_LINE_HEIGHT}px`;
  row.style.pointerEvents = "none";
  row.style.contain = "layout style paint";

  const content = document.createElement("div");
  content.className = "whitespace-pre";
  content.style.minHeight = `${HISTORY_LINE_HEIGHT}px`;
  content.style.willChange = "transform, opacity";
  content.style.color =
    line.kind === "added"
      ? `rgb(${diffAddRgb()} / 0.98)`
      : line.kind === "removed"
        ? `rgb(${diffDelRgb()} / 0.9)`
        : "";
  content.textContent = line.text === "" ? " " : line.text;

  row.appendChild(content);
  return { key: line.key, row, content };
};

// Place each row where `measureMotionLine` puts its line at `progress`.
// A row whose key is not its line's (the rows were built for another
// frame) is left where it is.
const placeTransitionRows = (
  rows: readonly TransitionRowElements[],
  lines: readonly MotionLine[],
  progress: number,
  direction: 1 | -1,
  staggerCount: number,
): void => {
  lines.forEach((line, index) => {
    const elements = rows[index];
    if (!elements || elements.key !== line.key) return;
    elements.row.style.top = `${measureMotionLine(line, progress, direction, staggerCount).top}px`;
  });
};

// Past this many lines on either side a workspace change is handed off
// without an animation: every line is a DOM row, and the line diff is
// quadratic in the line counts.
export const LINE_TRANSITION_MAX_LINES = 1000;

// One old → new line transition played once over `host`, for a file the
// editor did not change itself (the workspace's terminal did). The same
// rows, motion lines and gsap timeline the history viewer scrubs, driven
// by the clock instead of a scroll position, and sped up so it never
// runs longer than `navTweenDuration`'s cap however many lines changed.
// `onComplete` fires from the timeline's own onComplete — or at once,
// with no timeline, when there is nothing to animate: too many lines,
// no line added or removed, or motion lines that cannot be built (the
// WASM bridge not loaded yet, which is logged). The caller always gets
// to hand off.
export const playLineTransition = (
  host: HTMLElement,
  oldText: string,
  newText: string,
  onComplete: () => void,
): gsap.core.Timeline | null => {
  const lineCount = Math.max(
    splitLines(oldText).length,
    splitLines(newText).length,
  );
  if (lineCount > LINE_TRANSITION_MAX_LINES) {
    onComplete();
    return null;
  }

  let lines: MotionLine[];
  try {
    lines = buildMotionLines(oldText, newText);
  } catch (error) {
    console.warn("[HistoryViewer] workspace change not animated:", error);
    onComplete();
    return null;
  }
  if (lines.every((line) => line.kind === "unchanged")) {
    onComplete();
    return null;
  }

  const staggerCount = motionLineStaggerCount(lines);
  const rows = lines.map(buildTransitionRow);
  host.replaceChildren(...rows.map((r) => r.row));
  placeTransitionRows(rows, lines, 0, 1, staggerCount);

  const timeline = buildTransitionTimeline(rows, lines, 1);
  timeline.timeScale(
    Math.max(1, timeline.duration() / navTweenDuration(staggerCount)),
  );
  timeline.eventCallback("onUpdate", () => {
    placeTransitionRows(rows, lines, timeline.progress(), 1, staggerCount);
  });
  timeline.eventCallback("onComplete", onComplete);
  timeline.play();
  return timeline;
};

const renderTransition = (
  state: HistoryViewerState,
  frame: TransitionFrame,
): void => {
  if (!state.codeEl) return;
  state.codeEl.style.position = "relative";
  state.codeEl.style.height = `${transitionStageLineCount(frame) * HISTORY_LINE_HEIGHT}px`;
  const nextKey = transitionFrameKey(frame);

  if (
    state.transitionKey !== nextKey ||
    state.transitionRows.length !== frame.motionLines.length
  ) {
    // Capture highlighted HTML from the currently displayed static commit before
    // replacing it with transition rows. The static commit could be either the
    // "from" or "to" side of the transition depending on scroll direction, so we
    // check both and use the matching index (fromIndex or toIndex) to look up the
    // prior line's innerHTML.
    const fromCommitIndex = state.payload
      ? findCommitIndex(state.payload.commits, frame.from.hash)
      : -1;
    const toCommitIndex = state.payload
      ? findCommitIndex(state.payload.commits, frame.to.hash)
      : -1;
    const priorIsFrom =
      state.staticCommitIndex !== null &&
      state.staticCommitIndex === fromCommitIndex;
    const priorIsTo =
      state.staticCommitIndex !== null &&
      state.staticCommitIndex === toCommitIndex;
    const priorLineHtml: string[] = [];
    if ((priorIsFrom || priorIsTo) && state.codeEl.children.length > 0) {
      for (let i = 0; i < state.codeEl.children.length; i++) {
        const contentDiv = state.codeEl.children[i]
          ?.lastElementChild as HTMLElement | null;
        priorLineHtml.push(contentDiv?.innerHTML ?? "");
      }
    }

    state.transitionTimeline?.kill();
    state.transitionKey = nextKey;
    state.staticCommitIndex = null;
    state.renderVersion += 1;
    const renderVersion = state.renderVersion;
    const fragment = document.createDocumentFragment();
    state.transitionRows = frame.motionLines.map((line) => {
      const elements = buildTransitionRow(line);

      // Carry over highlighted HTML from the prior static rendering to avoid a
      // flash when the DOM swaps from static lines to transition rows.
      // - priorIsFrom: unchanged/removed lines map via fromIndex
      // - priorIsTo:   unchanged/added lines map via toIndex
      const carriedHtml =
        priorIsFrom &&
        (line.kind === "unchanged" || line.kind === "removed") &&
        line.fromIndex !== null
          ? (priorLineHtml[line.fromIndex] ?? "")
          : priorIsTo &&
              (line.kind === "unchanged" || line.kind === "added") &&
              line.toIndex !== null
            ? (priorLineHtml[line.toIndex] ?? "")
            : "";
      if (carriedHtml) elements.content.innerHTML = carriedHtml;

      fragment.appendChild(elements.row);
      return elements;
    });
    state.codeEl.replaceChildren(fragment);
    state.transitionTimeline = buildTransitionTimeline(
      state.transitionRows,
      frame.motionLines,
      frame.direction,
    );

    if (state.lang) {
      void Promise.all([
        historyHighlightRenderer(frame.fromContent, state.lang),
        historyHighlightRenderer(frame.toContent, state.lang),
      ]).then(([fromLines, toLines]) => {
        if (
          !state.codeEl ||
          state.transitionKey !== nextKey ||
          state.renderVersion !== renderVersion
        )
          return;
        frame.motionLines.forEach((line, index) => {
          const elements = state.transitionRows[index];
          if (!elements || elements.key !== line.key) return;
          const lineHtml =
            line.kind === "removed"
              ? fromLines[line.fromIndex ?? 0]
              : toLines[line.toIndex ?? 0];
          if (typeof lineHtml === "string") {
            elements.content.innerHTML = lineHtml;
          }
        });
      });
    }
  }

  state.currentFrame = frame;

  placeTransitionRows(
    state.transitionRows,
    frame.motionLines,
    frame.progress,
    frame.direction,
    frame.staggerCount,
  );

  state.transitionTimeline?.progress(frame.progress);
  updateStageIndicators(state);
};

const setNavState = (state: HistoryViewerState, _position: number): void => {
  // Nav shell was removed — commit list is now server-rendered in the sidebar.
  // Active state is managed by LiveView assigns, not client-side styling.
  if (!state.navShellEl) return;
};

// sameTimeline moved to history-viewer-pure.ts. Re-exported at the top
// of this file under its original name.

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
};

const currentTimelinePosition = (state: HistoryViewerState): number => {
  if (!state.payload || !state.scrollEl) return 0;
  const maxScroll = Math.max(
    1,
    state.scrollEl.scrollWidth - state.scrollEl.clientWidth,
  );
  const ratio = maxScroll <= 0 ? 0 : state.scrollEl.scrollLeft / maxScroll;
  return ratio * Math.max(state.payload.commits.length - 1, 0);
};

// Cache lookup with on-demand fetch. Returns null while the snapshot is
// in flight; the renderer treats null as "skip for now" and re-runs once
// the `history_content` push event populates the cache.
const HISTORY_FETCH_FLUSH_MS = 50;

const cachedCommitContent = (
  state: HistoryViewerState,
  commit: HistoryCommitFrame,
): string | null => {
  if (typeof commit.content === "string") return commit.content;
  const cached = state.contentCache.get(commit.hash);
  return cached === undefined ? null : cached;
};

const requestCommitContent = (
  state: HistoryViewerState,
  commit: HistoryCommitFrame,
): void => {
  if (state.contentCache.has(commit.hash)) return;
  if (state.pendingFetches.has(commit.hash)) return;
  // The path field on commits was optional in the type but the live
  // viewer always sets it (server payload includes per-commit path so
  // the rename-followed history fetches the right blob). Fall back to
  // an empty string so the server can recover via its own lookup.
  state.pendingFetches.set(commit.hash, commit.path ?? "");
  if (state.fetchTimer === null) {
    state.fetchTimer = window.setTimeout(() => {
      state.fetchTimer = null;
      const items = Array.from(state.pendingFetches, ([hash, path]) => ({
        hash,
        path,
      }));
      state.pendingFetches.clear();
      if (items.length === 0 || !state.hookRef) return;
      state.hookRef.pushEvent("fetchHistoryContent", { items });
    }, HISTORY_FETCH_FLUSH_MS);
  }
};

const resolveCommitContent =
  (state: HistoryViewerState) =>
  (commit: HistoryCommitFrame): string | null => {
    const found = cachedCommitContent(state, commit);
    if (found === null) requestCommitContent(state, commit);
    return found;
  };

const renderNearestCommit = (
  state: HistoryViewerState,
  commits: readonly HistoryCommitFrame[],
  position: number,
): void => {
  const nearestIndex = clamp(Math.round(position), 0, commits.length - 1);
  const commit = commits[nearestIndex];
  if (!commit) return;
  const content = cachedCommitContent(state, commit);
  if (content === null) {
    requestCommitContent(state, commit);
    return;
  }
  renderCommit(state, content, nearestIndex);
  setNavState(state, nearestIndex);
};

const alignNavShellToHash = (state: HistoryViewerState, hash: string): void => {
  const navShellEl = state.navShellEl;
  if (!navShellEl) return;

  const card = navShellEl.querySelector<HTMLElement>(
    `[data-history-waypoint="${hash}"]`,
  );
  if (!card) return;

  const maxScroll = Math.max(
    0,
    navShellEl.scrollWidth - navShellEl.clientWidth,
  );
  if (maxScroll <= 0) return;

  const target = clamp(
    card.offsetLeft + card.clientWidth - navShellEl.clientWidth,
    0,
    maxScroll,
  );
  navShellEl.scrollLeft = target;
};

const renderFromScroll = (
  _hook: LiveViewHookLike,
  state: HistoryViewerState,
): void => {
  if (!state.payload || !state.scrollEl || !state.codeEl) return;
  const commits = state.payload.commits;
  if (commits.length === 0) return;

  const maxScroll = Math.max(
    1,
    state.scrollEl.scrollWidth - state.scrollEl.clientWidth,
  );
  const ratio = maxScroll <= 0 ? 0 : state.scrollEl.scrollLeft / maxScroll;
  const position = ratio * Math.max(commits.length - 1, 0);
  if (state.lastPosition !== null) {
    const delta = position - state.lastPosition;
    if (Math.abs(delta) >= HISTORY_DIRECTION_EPSILON) {
      state.motionDirection = delta > 0 ? 1 : -1;
    }
  }
  state.lastPosition = position;
  const rounded = Math.round(position);
  const isWaypoint = isWaypointPosition(position, commits.length);
  setNavState(state, position);

  if (isWaypoint) {
    const commitIndex = clamp(rounded, 0, commits.length - 1);
    const commit = commits[commitIndex];
    if (!commit) return;
    const content = cachedCommitContent(state, commit);
    if (content === null) {
      requestCommitContent(state, commit);
      return;
    }
    renderCommit(state, content, commitIndex);
    return;
  }

  const frame = buildTransitionFrame(
    commits,
    position,
    state.motionDirection,
    resolveCommitContent(state),
  );
  if (!frame) {
    renderNearestCommit(state, commits, position);
    return;
  }
  renderTransition(state, frame);
};

const settleAtIndex = (
  hook: LiveViewHookLike,
  state: HistoryViewerState,
  index: number,
  hash: string,
  targetScrollLeft: number,
): void => {
  if (!state.scrollEl) return;
  state.scrollEl.scrollLeft = targetScrollLeft;
  state.syncing = false;
  state.syncTargetIndex = null;
  state.lastHash = hash;
  state.lastPosition = index;
  renderFromScroll(hook, state);
};

const scrollToHash = (
  hook: LiveViewHookLike,
  state: HistoryViewerState,
  hash: string,
  animate: boolean,
): void => {
  if (!state.payload || !state.scrollEl) return;
  const index = findCommitIndex(state.payload.commits, hash);
  if (index < 0) return;

  const maxScroll = Math.max(
    0,
    state.scrollEl.scrollWidth - state.scrollEl.clientWidth,
  );
  const target =
    progressFromIndex(index, state.payload.commits.length) * maxScroll;

  if (!animate) {
    state.motionDirection =
      state.lastPosition !== null && index < state.lastPosition ? -1 : 1;
    settleAtIndex(hook, state, index, hash, target);
    return;
  }

  const currentIndex =
    state.lastPosition !== null ? Math.round(state.lastPosition) : 0;
  const from =
    state.payload.commits[
      clamp(currentIndex, 0, state.payload.commits.length - 1)
    ];
  const to = state.payload.commits[index];
  // Stagger ramps the per-line tween offset against the actual delta. If
  // either snapshot isn't loaded yet, fall back to 1 — the animation
  // still plays at the default cadence and the cache lookup will trigger
  // a fetch we can use on subsequent frames.
  const fromContent = from ? cachedCommitContent(state, from) : null;
  const toContent = to ? cachedCommitContent(state, to) : null;
  const stagger =
    fromContent !== null && toContent !== null
      ? motionLineStaggerCount(buildMotionLines(fromContent, toContent))
      : 1;

  state.syncing = true;
  state.motionDirection =
    state.lastPosition !== null && index < state.lastPosition ? -1 : 1;
  state.syncTargetIndex = index;
  gsap.to(state.scrollEl, {
    scrollLeft: target,
    duration: navTweenDuration(stagger),
    ease: "sine.inOut",
    overwrite: true,
    onUpdate: () => {
      renderFromScroll(hook, state);
    },
    onComplete: () => {
      settleAtIndex(hook, state, index, hash, target);
    },
  });
};

const snapToNearest = (
  hook: LiveViewHookLike,
  state: HistoryViewerState,
): void => {
  if (!state.payload || !state.scrollEl || state.syncing) return;
  const maxScroll = Math.max(
    1,
    state.scrollEl.scrollWidth - state.scrollEl.clientWidth,
  );
  const ratio = maxScroll <= 0 ? 0 : state.scrollEl.scrollLeft / maxScroll;
  const position = ratio * Math.max(state.payload.commits.length - 1, 0);
  const nearest = Math.round(position);
  const commit =
    state.payload.commits[clamp(nearest, 0, state.payload.commits.length - 1)];
  if (!commit) return;
  scrollToHash(hook, state, commit.hash, false);
  state.lastHash = commit.hash;
};

const teardownHistoryViewer = (hook: LiveViewHookLike): void => {
  const state = getState(hook.el);
  if (state.snapTimer !== null) window.clearTimeout(state.snapTimer);
  if (state.fetchTimer !== null) window.clearTimeout(state.fetchTimer);
  state.transitionTimeline?.kill();
  if (state.scrollEl)
    state.scrollEl.removeEventListener("scroll", state.scrollHandler);
  if (state.stageEl)
    state.stageEl.removeEventListener("scroll", state.stageScrollHandler);
  if (state.stageEl)
    state.stageEl.removeEventListener("wheel", state.wheelHandler);
  if (state.scrollEl)
    state.scrollEl.removeEventListener("pointerdown", state.pointerDownHandler);
  document.removeEventListener("keydown", state.keyHandler);
  document.removeEventListener("click", state.waypointClickHandler);
  window.removeEventListener("pointerup", state.pointerUpHandler);
  state.contentCache.clear();
  state.pendingFetches.clear();
  state.hookRef = null;
  historyViewerState.delete(hook.el);
};

const buildHistoryViewer = (hook: LiveViewHookLike): void => {
  const state = getState(hook.el);
  state.payload =
    parseHistoryPayloadBase64(hook.el.getAttribute("data-history-b64")) ??
    parseHistoryPayload(hook.el.getAttribute("data-history"));
  state.navShellEl = hook.el.querySelector<HTMLElement>(
    "[data-history-nav-shell]",
  );
  state.scrollEl = hook.el.querySelector<HTMLElement>("[data-history-scroll]");
  state.stageEl = hook.el.querySelector<HTMLElement>("[data-history-stage]");
  state.codeEl = hook.el.querySelector<HTMLElement>("[data-history-code]");

  if (!state.payload || !state.scrollEl || !state.stageEl || !state.codeEl)
    return;
  state.lastHash = state.payload.activeHash;
  state.lastPosition = null;
  state.hookRef = hook;
  // Seed the cache with the active commit's content (the only snapshot
  // shipped in the initial payload). Every other commit's content is
  // requested lazily as the user scrolls / animates near it.
  state.contentCache.set(
    state.payload.activeHash,
    state.payload.activeContent ?? "",
  );
  // Server pushes one of these per `fetch_history_content` round-trip
  // with all snapshots in the batch. Once they land we re-render at the
  // current scroll position so any pending transition picks up the new
  // content without the user having to nudge the viewport.
  if (hook.handleEvent) {
    hook.handleEvent("historyContent", (raw) => {
      const payload = raw as {
        snapshots?: { hash: string; content: string }[];
      };
      if (!Array.isArray(payload.snapshots)) return;
      let added = false;
      for (const snap of payload.snapshots) {
        if (typeof snap.hash !== "string" || typeof snap.content !== "string")
          continue;
        if (state.contentCache.has(snap.hash)) continue;
        state.contentCache.set(snap.hash, snap.content);
        added = true;
      }
      if (added) {
        // Force a fresh render rather than a no-op early return inside
        // renderTransition's "same key" guard.
        state.transitionKey = null;
        renderFromScroll(hook, state);
      }
    });
  }

  state.scrollHandler = () => {
    renderFromScroll(hook, state);
    if (state.snapTimer !== null) window.clearTimeout(state.snapTimer);
    if (state.pointerDown) return;
    state.snapTimer = window.setTimeout(() => {
      snapToNearest(hook, state);
    }, 120);
  };

  state.stageScrollHandler = () => {
    updateStageIndicators(state);
  };

  // Document-level delegation catches waypoint clicks from the sidebar (outside
  // the hook element).  Setting lastHash immediately prevents syncHistoryViewer
  // from re-triggering the animation when the server roundtrip completes.
  state.waypointClickHandler = (e: Event) => {
    const target =
      e.target instanceof Element
        ? e.target.closest<HTMLElement>("[data-history-waypoint]")
        : null;
    if (!target) return;
    const hash = target.dataset["historyWaypoint"];
    if (hash && hash !== state.lastHash) {
      state.lastHash = hash;
      scrollToHash(hook, state, hash, true);
    }
  };
  document.addEventListener("click", state.waypointClickHandler);

  state.wheelHandler = (event: WheelEvent) => {
    if (!state.scrollEl) return;
    const horizontalDelta =
      Math.abs(event.deltaX) > 0
        ? event.deltaX
        : event.shiftKey
          ? event.deltaY
          : 0;
    if (horizontalDelta === 0) return;
    event.preventDefault();
    state.scrollEl.scrollLeft += horizontalDelta;
    renderFromScroll(hook, state);
    if (state.snapTimer !== null) window.clearTimeout(state.snapTimer);
    state.snapTimer = window.setTimeout(() => {
      snapToNearest(hook, state);
    }, 120);
  };

  state.keyHandler = (event: KeyboardEvent) => {
    if (
      !state.payload ||
      state.payload.commits.length === 0 ||
      isEditableTarget(event.target)
    )
      return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    const position = currentTimelinePosition(state);
    const nearest = Math.round(position);
    const delta = event.key === "ArrowLeft" ? -1 : 1;
    const nextIndex = clamp(
      nearest + delta,
      0,
      state.payload.commits.length - 1,
    );
    const commit = state.payload.commits[nextIndex];
    if (commit) scrollToHash(hook, state, commit.hash, true);
  };

  state.pointerDownHandler = () => {
    state.pointerDown = true;
    if (state.snapTimer !== null) {
      window.clearTimeout(state.snapTimer);
      state.snapTimer = null;
    }
  };

  state.pointerUpHandler = () => {
    if (!state.pointerDown) return;
    state.pointerDown = false;
    if (state.snapTimer !== null) window.clearTimeout(state.snapTimer);
    state.snapTimer = window.setTimeout(() => {
      snapToNearest(hook, state);
    }, 0);
  };

  state.scrollEl.addEventListener("scroll", state.scrollHandler, {
    passive: true,
  });
  state.scrollEl.addEventListener("pointerdown", state.pointerDownHandler);
  state.stageEl.addEventListener("scroll", state.stageScrollHandler, {
    passive: true,
  });
  state.stageEl.addEventListener("wheel", state.wheelHandler, {
    passive: false,
  });
  document.addEventListener("keydown", state.keyHandler);
  window.addEventListener("pointerup", state.pointerUpHandler);
  const fallbackHash =
    state.payload.commits[state.payload.commits.length - 1]?.hash;
  const initialHash = state.payload.activeHash || fallbackHash;
  if (initialHash) {
    alignNavShellToHash(state, initialHash);
    scrollToHash(hook, state, initialHash, false);
  }
};

const syncHistoryViewer = (hook: LiveViewHookLike): boolean => {
  const state = getState(hook.el);
  const nextPayload =
    parseHistoryPayloadBase64(hook.el.getAttribute("data-history-b64")) ??
    parseHistoryPayload(hook.el.getAttribute("data-history"));

  if (!sameTimeline(state.payload, nextPayload) || !nextPayload) return false;

  state.payload = nextPayload;

  if (nextPayload.activeHash !== state.lastHash) {
    // The waypoint click handler already started the animation client-side
    // and set lastHash.  This path only runs if the server changed the hash
    // without a prior click (e.g. URL navigation), so use an instant swap.
    scrollToHash(hook, state, nextPayload.activeHash, false);
    state.lastHash = nextPayload.activeHash;
  } else {
    const activeIndex = findCommitIndex(
      nextPayload.commits,
      nextPayload.activeHash,
    );
    if (activeIndex >= 0) setNavState(state, activeIndex);
  }

  return true;
};

export const HistoryViewerHook = {
  mounted(this: LiveViewHookLike) {
    buildHistoryViewer(this);
  },
  updated(this: LiveViewHookLike) {
    if (syncHistoryViewer(this)) return;
    teardownHistoryViewer(this);
    buildHistoryViewer(this);
  },
  destroyed(this: LiveViewHookLike) {
    teardownHistoryViewer(this);
  },
};
